/**
 * The arithmetic behind every chart in the suite, kept out of the components
 * so it can be reasoned about — and run — on its own.
 *
 * No imports, deliberately, and no JSX. Node 24 strips types, so a module with
 * neither runs directly under `node charts-scale.ts`, which is the only
 * unit-test mechanism the TypeScript side of this workspace has (there is no
 * test runner anywhere in it). The precedent is
 * `apps/admin-portal/src/screens/model-usage.ts`; the self-check block at the
 * bottom of this file is the part that precedent was missing.
 *
 * Two rules run through all of it, and both exist because the failure they
 * prevent is invisible:
 *
 *   * **A gap is not a zero.** A point whose value is `null` was NOT MEASURED.
 *     Drawn as zero it is a crash; interpolated over it is a straight line
 *     through days that never happened. `polylinePath` therefore BREAKS the
 *     path at a gap rather than closing it, and `describeSeries` counts the
 *     gaps out loud. (Days the API returns as explicit zeros are real zeros
 *     and are drawn as zeros — see AnalyticsSeriesPoint in admin-client.ts.)
 *   * **No scale ever divides by a zero-width domain.** A constant series and
 *     a single-point series both produce `max === min`, and `(v - min) / 0` is
 *     `NaN`, which reaches the DOM as an invalid `d` attribute and renders as
 *     nothing at all — a chart that is silently blank. `scaleLinear` answers
 *     the middle of its range instead, and `padExtent` widens the band so the
 *     constant line sits in the middle of a range whose height means nothing.
 */
/**
 * The extent of the finite values in a series, or null when there are none.
 *
 * `null`, `undefined`, `NaN` and the infinities are SKIPPED, not coerced: a
 * missing measurement pulled to zero moves the floor of every chart it appears
 * in, and the reader has no way to tell that from a quiet day.
 */
export function extentOf(values) {
    let min = 0;
    let max = 0;
    let seen = false;
    for (const value of values) {
        if (value === null || value === undefined || !Number.isFinite(value)) {
            continue;
        }
        if (!seen) {
            min = value;
            max = value;
            seen = true;
            continue;
        }
        if (value < min) {
            min = value;
        }
        if (value > max) {
            max = value;
        }
    }
    return seen ? { min, max } : null;
}
/**
 * Widen the extent to reach zero.
 *
 * Bars are measured from a baseline, so a bar chart whose axis starts at the
 * smallest observed value draws 51 as three times the height of 49. Lines are
 * about shape and do not want this; that is why it is a separate helper rather
 * than something `extentOf` does.
 */
export function includeZero(extent) {
    return { min: Math.min(extent.min, 0), max: Math.max(extent.max, 0) };
}
/**
 * Give a zero-width extent a width, so a constant series has somewhere to be
 * drawn. An extent that already has width is returned untouched.
 *
 * The value ends up in the MIDDLE of the widened band on purpose. Anchoring it
 * to the top or the bottom would make the band's height look like a reading;
 * in the middle it plainly says "this did not vary" and the axis says what it
 * did not vary from.
 */
export function padExtent(extent) {
    if (extent.max > extent.min) {
        return extent;
    }
    // A constant zero series has no magnitude to scale from, so it gets a unit
    // band. Anything else gets half its own magnitude on each side, which keeps
    // the padding proportionate whether the series sits at 3 or at 3,000,000.
    const magnitude = Math.abs(extent.min) || 1;
    return { min: extent.min - magnitude / 2, max: extent.max + magnitude / 2 };
}
/**
 * A linear scale from `domain` onto `[from, to]`.
 *
 * `from` and `to` are a pair rather than an Extent because a screen y axis is
 * INVERTED — `scaleLinear(domain, height - pad, pad)` — and calling the larger
 * of those two `min` would be a lie the next reader has to decode.
 *
 * A zero-width domain answers the middle of the range for every input. That is
 * the only safe answer: there is no ratio, and the alternatives are `NaN` (an
 * invisible chart) or pinning to one end (a constant series that looks like a
 * maximum).
 */
export function scaleLinear(domain, from, to) {
    const span = domain.max - domain.min;
    const middle = (from + to) / 2;
    if (!(span > 0) || !Number.isFinite(span)) {
        return () => middle;
    }
    const ratio = (to - from) / span;
    const start = domain.min;
    return (value) => from + (value - start) * ratio;
}
/**
 * The step between axis ticks: a 1, 2, 5 or 10 times a power of ten, chosen so
 * roughly `count` of them span the range.
 *
 * The thresholds are the geometric midpoints between the candidates —
 * sqrt(2) between 1 and 2, sqrt(10) between 2 and 5, sqrt(50) between 5 and 10
 * — so the chosen step is the one whose tick COUNT is closest to what was
 * asked in ratio terms. Picking "the smallest candidate at least as large as
 * the raw step" instead is the obvious rule and it is wrong: a range of 97
 * asked for 4 ticks has a raw step of 24.25, which rounds up to 50 and leaves
 * an axis with two labels on it.
 *
 * Returns 0 when there is no meaningful step, which callers read as "no ticks".
 */
export function tickStep(span, count) {
    if (!(span > 0) || !Number.isFinite(span) || !(count >= 1)) {
        return 0;
    }
    const raw = span / Math.floor(count);
    const power = Math.floor(Math.log10(raw));
    const error = raw / Math.pow(10, power);
    const factor = error >= Math.sqrt(50) ? 10 : error >= Math.sqrt(10) ? 5 : error >= Math.sqrt(2) ? 2 : 1;
    return factor * Math.pow(10, power);
}
/**
 * Round ticks that land inside `extent`, ascending.
 *
 * Every tick is rounded to the step's own precision before it is returned.
 * Accumulating `n * 0.2` in binary gives 0.6000000000000001, and an axis label
 * reading "0.6000000000000001" is the kind of defect that ships because nobody
 * looks at the third gridline.
 *
 * A zero-width extent yields the single value it is: an axis for a constant
 * series still has to say what the constant is.
 */
export function niceTicks(extent, count) {
    const step = tickStep(extent.max - extent.min, count);
    if (step <= 0) {
        return Number.isFinite(extent.min) && extent.min === extent.max ? [extent.min] : [];
    }
    // toFixed refuses more than 100 digits; a step that small cannot be labelled
    // legibly anyway, so the clamp costs nothing real.
    const decimals = Math.min(100, Math.max(0, -Math.floor(Math.log10(step))));
    // The epsilons keep a tick that lands exactly on a bound from being dropped
    // by the rounding error in the division that found it.
    const first = Math.ceil(extent.min / step - 1e-9);
    const last = Math.floor(extent.max / step + 1e-9);
    const ticks = [];
    for (let n = first; n <= last; n++) {
        ticks.push(Number((n * step).toFixed(decimals)));
    }
    return ticks;
}
/** Two decimals is a tenth of a pixel on a 1000-unit viewBox — below anything
 *  a screen can draw, and it keeps the `d` attribute short enough to read. */
function coordinate(value) {
    return String(Math.round(value * 100) / 100);
}
/**
 * The `d` attribute for a polyline through `points`, with a fresh subpath
 * after every gap.
 *
 * A run of a single point emits a zero-length line (`M x y L x y`) rather than
 * a bare `M`, which draws nothing at all. With `stroke-linecap="round"` that
 * renders as a dot: one measured day between two unmeasured ones is a fact,
 * and it should be visible.
 *
 * A non-finite coordinate is treated as a gap. It can only arrive from a scale
 * handed a value the series said was measured but was not, and drawing `NaN`
 * invalidates the whole attribute — one bad point would blank the chart.
 */
export function polylinePath(points) {
    const parts = [];
    let runLength = 0;
    let last = null;
    const closeRun = () => {
        if (runLength === 1 && last !== null) {
            parts.push(`L${coordinate(last.x)} ${coordinate(last.y)}`);
        }
        runLength = 0;
        last = null;
    };
    for (const point of points) {
        if (point === null || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
            closeRun();
            continue;
        }
        parts.push(`${runLength === 0 ? "M" : "L"}${coordinate(point.x)} ${coordinate(point.y)}`);
        runLength += 1;
        last = point;
    }
    closeRun();
    return parts.join("");
}
/** The measured points of a series, in order, with the gaps dropped. */
export function measuredPoints(points) {
    const measured = [];
    for (const point of points) {
        if (point.value !== null && Number.isFinite(point.value)) {
            measured.push({ label: point.label, value: point.value });
        }
    }
    return measured;
}
/**
 * The sentence a screen reader hears instead of "graphic".
 *
 * It carries the four things a sighted reader takes from a glance at a line —
 * how long it is, where it starts and ends, and its low and high — plus the
 * one thing a glance does NOT give them: how much of the window was measured
 * at all. The full numbers are in the visually-hidden table beside the chart;
 * this is the summary, and a summary that omitted the gaps would be describing
 * a denser series than the one on screen.
 */
export function describeSeries(name, points, format = (value) => String(value)) {
    const measured = measuredPoints(points);
    if (measured.length === 0) {
        return `${name}: no data.`;
    }
    let lowest = measured[0];
    let highest = measured[0];
    for (const point of measured) {
        // Strict comparisons, so a tie names the FIRST point that reached the
        // value rather than the last — one rule, stated, instead of whichever one
        // the loop happened to have.
        if (point.value < lowest.value) {
            lowest = point;
        }
        if (point.value > highest.value) {
            highest = point;
        }
    }
    const first = measured[0];
    const last = measured[measured.length - 1];
    const coverage = measured.length === points.length
        ? `${measured.length} points`
        : `${measured.length} of ${points.length} points measured`;
    return (`${name}: ${coverage}, ${first.label} to ${last.label}. ` +
        `Starts at ${format(first.value)}, ends at ${format(last.value)}. ` +
        `Lowest ${format(lowest.value)} at ${lowest.label}, highest ${format(highest.value)} at ${highest.label}.`);
}
/* ------------------------------------------------------------------------ *
 * Categorical colour: the slot contract.
 *
 * The palette lives in styles.css as --esm-chart-1 … --esm-chart-6 plus the
 * semantic pair. Two rules make it safe, and both are enforced here rather
 * than left to each screen:
 *
 *   * **Slots are assigned in fixed order and NEVER cycled.** A seventh series
 *     handed slot 1 again is two different things wearing one colour, which is
 *     worse than no colour at all. Past the sixth, `chartSlotColor` answers the
 *     neutral --esm-chart-other and `foldToOther` collapses the tail into one
 *     stated row, so the reader is told rather than misled.
 *   * **The semantic pair and the categorical slots never appear in the same
 *     chart.** --esm-chart-positive is a green and --esm-chart-6 is a green;
 *     they measure ~10 ΔE apart, which is below the 15 two IDENTITIES need.
 *     They are safe because one chart answers "which of these" and the other
 *     answers "did it work", and no chart asks both.
 * ------------------------------------------------------------------------ */
/** How many identities the categorical palette can name. */
export const CHART_SLOTS = 6;
/** The CSS colour for categorical slot `index`, 0-based. */
export function chartSlotColor(index) {
    if (!Number.isInteger(index) || index < 0 || index >= CHART_SLOTS) {
        return "var(--esm-chart-other)";
    }
    return `var(--esm-chart-${index + 1})`;
}
/** Good news. Never used in the same chart as a categorical slot. */
export const CHART_POSITIVE = "var(--esm-chart-positive)";
/** Bad news. Its hue is red-green against CHART_POSITIVE and a deuteranope
 *  cannot separate the two, so wherever the pair is used the position in the
 *  stack and the legend's words carry the meaning and the hue only repeats
 *  it. */
export const CHART_NEGATIVE = "var(--esm-chart-negative)";
/**
 * Collapse everything past the largest `max - 1` into a single "Other" row.
 *
 * The kept rows come back in INPUT ORDER, not in rank order. Slot colours are
 * assigned by position, so returning them ranked would make the colour follow
 * the rank — and then a filter that drops one series repaints all the
 * survivors. The caller decides the order once; this only decides membership.
 */
export function foldToOther(points, max, otherLabel = "Other") {
    const limit = Math.max(1, Math.floor(max));
    if (points.length <= limit) {
        return { points: points.slice(), folded: 0, droppedUnmeasured: 0 };
    }
    const measured = [];
    points.forEach((point, index) => {
        if (point.value !== null && Number.isFinite(point.value)) {
            measured.push({ index, value: point.value });
        }
    });
    // Descending by value; ties keep the earlier point, so the choice is the
    // caller's order rather than the sort's.
    measured.sort((a, b) => b.value - a.value || a.index - b.index);
    const keep = new Set(measured.slice(0, limit - 1).map((entry) => entry.index));
    const kept = [];
    let other = 0;
    let folded = 0;
    let droppedUnmeasured = 0;
    points.forEach((point, index) => {
        if (keep.has(index)) {
            kept.push(point);
            return;
        }
        if (point.value === null || !Number.isFinite(point.value)) {
            droppedUnmeasured += 1;
            return;
        }
        other += point.value;
        folded += 1;
    });
    if (folded > 0) {
        kept.push({ label: otherLabel, value: other });
    }
    return { points: kept, folded, droppedUnmeasured };
}
/**
 * `count` evenly spaced bands across `[from, to]`, each centred in its slot.
 *
 * When the slot is too tight to hold the gap — thirty days across a card —
 * the bars keep a fixed fraction of the slot instead of going to zero width.
 * A chart that silently stops drawing above some series count is the failure
 * this avoids; a thin bar still reports its height.
 */
export function bandPositions(count, from, to, options = {}) {
    const { gap = 2, maxThickness = 24 } = options;
    if (!Number.isInteger(count) || count < 1 || !Number.isFinite(from) || !Number.isFinite(to)) {
        return [];
    }
    const slot = (to - from) / count;
    if (!(slot > 0)) {
        return [];
    }
    const roomy = Math.min(slot - gap, maxThickness);
    const size = roomy > 0 ? roomy : slot * 0.6;
    const bands = [];
    for (let index = 0; index < count; index++) {
        const start = from + index * slot + (slot - size) / 2;
        bands.push({ start, size, center: start + size / 2 });
    }
    return bands;
}
/**
 * Which indices out of `count` get a written axis label, at most `maxLabels`
 * of them, always including the LAST.
 *
 * Counting back from the end rather than forward from the start is what makes
 * the final day labelled without a second rule bolted on — and a second rule
 * is how the last two labels end up on top of each other.
 */
export function labelledIndices(count, maxLabels) {
    if (!Number.isInteger(count) || count < 1 || !(maxLabels >= 1)) {
        return [];
    }
    const stride = Math.max(1, Math.ceil(count / maxLabels));
    const indices = [];
    for (let index = count - 1; index >= 0; index -= stride) {
        indices.push(index);
    }
    return indices.reverse();
}
/** Lay `parts` end to end from zero, in order. */
export function stackValues(label, parts) {
    const segments = [];
    let cursor = 0;
    let unmeasured = 0;
    let undrawable = 0;
    for (const part of parts) {
        if (part.value === null || !Number.isFinite(part.value)) {
            segments.push(null);
            unmeasured += 1;
            continue;
        }
        if (part.value < 0) {
            segments.push(null);
            undrawable += 1;
            continue;
        }
        const start = cursor;
        cursor += part.value;
        segments.push({ label: part.label, value: part.value, start, end: cursor });
    }
    return { label, segments, total: cursor, unmeasured, undrawable };
}
/**
 * Transpose per-series readings into one Stack per bar.
 *
 * The API hands out a series at a time (`succeeded[]`, `failed[]`); a stacked
 * bar is assembled a bar at a time. A series shorter than `labels` yields a
 * `null` for the bars it does not reach — which is "not measured", the only
 * honest reading of an absent entry, and NOT a zero.
 */
export function stackBars(labels, series) {
    return labels.map((label, index) => stackValues(label, series.map((entry) => ({ label: entry.name, value: entry.points[index]?.value ?? null }))));
}
/**
 * The value-axis domain for bars: always anchored at zero, never zero-width.
 *
 * This is `padExtent`'s counterpart, not a synonym for it. A constant LINE
 * belongs in the middle of a padded band, because a line is about shape. A
 * constant bar does not: its length IS the reading, so the axis has to start
 * at zero or 51 draws as three times 49. An all-zero series therefore gets
 * `[0, 1]` — empty bars against a real axis — rather than `[-0.5, 0.5]`,
 * which would put the baseline halfway up the chart.
 */
export function barDomain(values) {
    const raw = includeZero(extentOf(values) ?? { min: 0, max: 0 });
    return raw.max > raw.min ? raw : { min: 0, max: 1 };
}
/**
 * A rectangle with the two corners on `side` rounded.
 *
 * Returns "" for a bar with no area. A zero-height rect still has a `d` a
 * browser will happily accept and draw as nothing, but an empty string is the
 * one that cannot pick up a stroke or a hover target it has no business
 * having.
 */
export function barPath(x, y, width, height, radius, side = "none") {
    if (![x, y, width, height, radius].every((value) => Number.isFinite(value))) {
        return "";
    }
    if (!(width > 0) || !(height > 0)) {
        return "";
    }
    const r = side === "none" ? 0 : Math.max(0, Math.min(radius, width / 2, height / 2));
    const x0 = coordinate(x);
    const y0 = coordinate(y);
    const x1 = coordinate(x + width);
    const y1 = coordinate(y + height);
    if (r <= 0) {
        return `M${x0} ${y0}L${x1} ${y0}L${x1} ${y1}L${x0} ${y1}Z`;
    }
    const xr0 = coordinate(x + r);
    const xr1 = coordinate(x + width - r);
    const yr0 = coordinate(y + r);
    const yr1 = coordinate(y + height - r);
    switch (side) {
        case "top":
            return `M${x0} ${y1}L${x0} ${yr0}Q${x0} ${y0} ${xr0} ${y0}L${xr1} ${y0}Q${x1} ${y0} ${x1} ${yr0}L${x1} ${y1}Z`;
        case "bottom":
            return `M${x0} ${y0}L${x1} ${y0}L${x1} ${yr1}Q${x1} ${y1} ${xr1} ${y1}L${xr0} ${y1}Q${x0} ${y1} ${x0} ${yr1}Z`;
        case "right":
            return `M${x0} ${y0}L${xr1} ${y0}Q${x1} ${y0} ${x1} ${yr0}L${x1} ${yr1}Q${x1} ${y1} ${xr1} ${y1}L${x0} ${y1}Z`;
        default:
            return `M${x1} ${y0}L${x1} ${y1}L${xr0} ${y1}Q${x0} ${y1} ${x0} ${yr1}L${x0} ${yr0}Q${x0} ${y0} ${xr0} ${y0}Z`;
    }
}
/** A full turn. Angles here are radians from twelve o'clock, clockwise. */
const TAU = Math.PI * 2;
function circlePoint(cx, cy, r, angle) {
    // sin on x and -cos on y is the rotation that puts 0 at the top and runs
    // clockwise, which is the direction a reader's eye starts a ring in.
    return { x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) };
}
function ringPath(cx, cy, r, sweep) {
    const top = coordinate(cy - r);
    const bottom = coordinate(cy + r);
    const cxs = coordinate(cx);
    const rs = coordinate(r);
    // Two half-arcs: a single elliptical arc of exactly 360 degrees has the same
    // start and end point and SVG draws nothing at all for it.
    return `M${cxs} ${top}A${rs} ${rs} 0 1 ${sweep} ${cxs} ${bottom}A${rs} ${rs} 0 1 ${sweep} ${cxs} ${top}Z`;
}
/**
 * The `d` for one ring segment between two angles.
 *
 * A span of a whole turn becomes two counter-wound circles, so the hole is
 * punched by the fill rule instead of by an arc that would collapse to a
 * point. `inner <= 0` gives a solid wedge.
 */
export function arcPath(cx, cy, outer, inner, start, end) {
    if (![cx, cy, outer, inner, start, end].every((value) => Number.isFinite(value))) {
        return "";
    }
    if (!(outer > 0)) {
        return "";
    }
    const span = end - start;
    if (!(span > 0)) {
        return "";
    }
    const hole = Math.max(0, Math.min(inner, outer));
    if (span >= TAU - 1e-9) {
        return ringPath(cx, cy, outer, 1) + (hole > 0 ? ringPath(cx, cy, hole, 0) : "");
    }
    const large = span > Math.PI ? 1 : 0;
    const o0 = circlePoint(cx, cy, outer, start);
    const o1 = circlePoint(cx, cy, outer, end);
    const os = coordinate(outer);
    const head = `M${coordinate(o0.x)} ${coordinate(o0.y)}A${os} ${os} 0 ${large} 1 ${coordinate(o1.x)} ${coordinate(o1.y)}`;
    if (hole <= 0) {
        return `M${coordinate(cx)} ${coordinate(cy)}L${coordinate(o0.x)} ${coordinate(o0.y)}A${os} ${os} 0 ${large} 1 ${coordinate(o1.x)} ${coordinate(o1.y)}Z`;
    }
    const i1 = circlePoint(cx, cy, hole, end);
    const i0 = circlePoint(cx, cy, hole, start);
    const hs = coordinate(hole);
    return (`${head}L${coordinate(i1.x)} ${coordinate(i1.y)}` +
        `A${hs} ${hs} 0 ${large} 0 ${coordinate(i0.x)} ${coordinate(i0.y)}Z`);
}
/**
 * Divide a turn between the measured, non-negative parts.
 *
 * A total of zero produces NO arcs. There is nothing to divide, and a ring
 * drawn anyway — one slice at 100% of nothing — is a picture of a busy day
 * that did not happen.
 */
export function donutArcs(points, options = {}) {
    const { gapAngle = 0 } = options;
    const parts = [];
    let unmeasured = 0;
    let undrawable = 0;
    let total = 0;
    points.forEach((point, index) => {
        if (point.value === null || !Number.isFinite(point.value)) {
            unmeasured += 1;
            return;
        }
        if (point.value < 0) {
            undrawable += 1;
            return;
        }
        parts.push({ label: point.label, value: point.value, index });
        total += point.value;
    });
    const arcs = points.map(() => null);
    let tooSmall = 0;
    if (total > 0) {
        // A lone slice is the whole ring and has no neighbour to be separated
        // from, so it takes no inset — otherwise 100% draws with a notch in it.
        const inset = parts.length <= 1 ? 0 : Math.max(0, gapAngle) / 2;
        let angle = 0;
        for (const part of parts) {
            const span = (part.value / total) * TAU;
            const start = angle;
            angle += span;
            const bite = Math.min(inset, span / 2);
            const drawable = span > gapAngle;
            if (part.value > 0 && !drawable) {
                tooSmall += 1;
            }
            arcs[part.index] = {
                label: part.label,
                value: part.value,
                share: part.value / total,
                startAngle: start + bite,
                endAngle: angle - bite,
                drawable,
            };
        }
    }
    return { arcs, measured: parts.length, total, unmeasured, undrawable, tooSmall };
}
/** `value` as a percentage of `total`, one decimal place, or null when there
 *  is no whole to be a part of. Zero out of zero is not 0% — it is nothing at
 *  all, and a screen that prints "0%" has stated a measurement. */
export function sharePercent(value, total) {
    if (!Number.isFinite(value) || !Number.isFinite(total) || !(total > 0)) {
        return null;
    }
    return Math.round((value / total) * 1000) / 10;
}
/** "1 bar" / "2 bars". A summary that says "1 bars" reads as generated text
 *  and a reader stops trusting the rest of the sentence. Exported because the
 *  components write sentences too, and a second copy of this is a second place
 *  for the same defect. */
export function plural(count, one, many) {
    return `${count} ${count === 1 ? one : many}`;
}
/* ------------------------------------------------------------------------ *
 * Summaries. One per chart shape, all built here so a screen reader gets the
 * same grammar everywhere and no component hand-writes a second format.
 * ------------------------------------------------------------------------ */
/**
 * The aria-label for a chart of named things compared against each other.
 *
 * Unlike `describeSeries` it does NOT say "starts at / ends at": the order of
 * a category axis is the caller's choice, and calling the first one a start
 * would report that choice as a finding.
 */
export function describeCategories(name, points, format = (value) => String(value)) {
    const measured = measuredPoints(points);
    if (measured.length === 0) {
        return `${name}: no data.`;
    }
    let total = 0;
    let highest = measured[0];
    let lowest = measured[0];
    for (const point of measured) {
        total += point.value;
        if (point.value > highest.value) {
            highest = point;
        }
        if (point.value < lowest.value) {
            lowest = point;
        }
    }
    const coverage = measured.length === points.length
        ? plural(measured.length, "category", "categories")
        : `${measured.length} of ${points.length} categories measured`;
    if (measured.length === 1) {
        return `${name}: ${coverage}. ${highest.label} at ${format(highest.value)}.`;
    }
    return (`${name}: ${coverage}, ${format(total)} in total. ` +
        `Highest ${highest.label} at ${format(highest.value)}, lowest ${lowest.label} at ${format(lowest.value)}.`);
}
/** The aria-label for a ring: what the whole is, and what the ends of it are. */
export function describeComposition(name, points, format = (value) => String(value), options = {}) {
    // The SAME options the ring was drawn with. Given the default of no gap,
    // nothing is ever too thin, and the label would report a complete picture
    // while the legend beside it marks slices as undrawn.
    const { arcs, measured, total, unmeasured, undrawable, tooSmall } = donutArcs(points, options);
    if (points.length === 0) {
        return `${name}: no data.`;
    }
    if (measured === 0) {
        return `${name}: nothing measured in this window.`;
    }
    if (total === 0) {
        return `${name}: ${plural(measured, "part", "parts")}, all zero — there is no whole to divide.`;
    }
    const slices = arcs.filter((arc) => arc !== null);
    let largest = slices[0];
    let smallest = slices[0];
    for (const arc of slices) {
        if (arc.value > largest.value) {
            largest = arc;
        }
        if (arc.value < smallest.value) {
            smallest = arc;
        }
    }
    const missing = unmeasured + undrawable;
    const coverage = missing === 0 ? plural(slices.length, "part", "parts") : `${slices.length} of ${points.length} parts measured`;
    const thin = tooSmall > 0 ? ` ${tooSmall} too thin to draw; the table has them.` : "";
    const ends = slices.length === 1
        ? ` ${largest.label} is all of it.`
        : ` Largest ${largest.label}, ${format(largest.value)}, ${sharePercent(largest.value, total)}%.` +
            ` Smallest ${smallest.label}, ${format(smallest.value)}, ${sharePercent(smallest.value, total)}%.`;
    return `${name}: ${coverage}, ${format(total)} in total.${ends}${thin}`;
}
/** The aria-label for bars made of parts: how many bars, over what, made of
 *  what, in which order — the order being the encoding that survives when the
 *  hues do not. */
export function describeStacks(name, seriesNames, stacks, format = (value) => String(value)) {
    if (stacks.length === 0) {
        return `${name}: no data.`;
    }
    const first = stacks[0];
    const final = stacks[stacks.length - 1];
    let grand = 0;
    let largest = first;
    let missing = 0;
    for (const stack of stacks) {
        grand += stack.total;
        if (stack.total > largest.total) {
            largest = stack;
        }
        missing += stack.unmeasured + stack.undrawable;
    }
    const span = stacks.length === 1 ? first.label : `${first.label} to ${final.label}`;
    const order = seriesNames.length > 0 ? ` Stacked bottom to top: ${seriesNames.join(", ")}.` : "";
    const gaps = missing > 0 ? ` ${missing} of ${stacks.length * seriesNames.length} segments not measured.` : "";
    return (`${name}: ${plural(stacks.length, "bar", "bars")}, ${span}, ${format(grand)} in total.${order}` +
        ` Largest ${largest.label} at ${format(largest.total)}.${gaps}`);
}
/**
 * Transpose per-series readings into one Group per category.
 *
 * `stackBars`' twin, deliberately down to the shape of the loop: a series
 * shorter than `labels` yields `null` for the categories it does not reach,
 * which is "not measured" and never a zero.
 */
export function groupBars(labels, series) {
    return labels.map((label, index) => {
        const bars = [];
        let unmeasured = 0;
        for (const entry of series) {
            const value = entry.points[index]?.value ?? null;
            if (value === null || !Number.isFinite(value)) {
                bars.push(null);
                unmeasured += 1;
                continue;
            }
            bars.push({ label: entry.name, value });
        }
        return { label, bars, unmeasured };
    });
}
/**
 * The aria-label for grouped bars.
 *
 * Per-SERIES totals rather than per-group ones, which is the opposite of
 * `describeStacks`. A group's bars do not compose anything, so their sum is a
 * number about nothing; what a reader wants spoken is how each series did over
 * the whole span, and the table underneath carries the cell-by-cell figures.
 */
export function describeGroups(name, seriesNames, groups, format = (value) => String(value)) {
    if (groups.length === 0 || seriesNames.length === 0) {
        return `${name}: no data.`;
    }
    const first = groups[0];
    const final = groups[groups.length - 1];
    const span = groups.length === 1 ? first.label : `${first.label} to ${final.label}`;
    const totals = seriesNames.map((seriesName, index) => {
        let sum = 0;
        let measured = 0;
        for (const group of groups) {
            const bar = group.bars[index];
            if (bar) {
                sum += bar.value;
                measured += 1;
            }
        }
        return measured === 0 ? `${seriesName} not measured` : `${seriesName} ${format(sum)}`;
    });
    let missing = 0;
    for (const group of groups) {
        missing += group.unmeasured;
    }
    const gaps = missing > 0 ? ` ${missing} of ${groups.length * seriesNames.length} bars not measured.` : "";
    return (`${name}: ${plural(groups.length, "group", "groups")} of ` +
        `${plural(seriesNames.length, "bar", "bars")}, ${span}, sharing one axis.` +
        ` Totals: ${totals.join(", ")}.${gaps}`);
}
/* ------------------------------------------------------------------------ *
 * Self-check: `node packages/ts/ui-components/src/charts-scale.ts`
 *
 * `import.meta.main` is Node 24's own "was this file the entry point" flag.
 * It is read through a cast because TypeScript 5.7's ImportMeta does not
 * declare it yet, and a `declare global` augmentation in a package that emits
 * .d.ts would push that lie onto every consumer. In a browser bundle the
 * property is simply undefined and the block never runs.
 * ------------------------------------------------------------------------ */
function selfCheck() {
    let passed = 0;
    const check = (name, actual, expected) => {
        const got = JSON.stringify(actual);
        const want = JSON.stringify(expected);
        if (got !== want) {
            throw new Error(`charts-scale self-check FAILED: ${name}\n  expected ${want}\n  actual   ${got}`);
        }
        passed += 1;
    };
    // extentOf skips everything that is not a finite number, and says so by
    // answering null rather than {0, 0} — which a caller cannot tell from a
    // series that really is all zeros.
    check("extentOf empty", extentOf([]), null);
    check("extentOf all gaps", extentOf([null, undefined, Number.NaN, Number.POSITIVE_INFINITY]), null);
    check("extentOf skips non-finite", extentOf([3, Number.NaN, -1, Number.POSITIVE_INFINITY, 5]), { min: -1, max: 5 });
    check("extentOf constant", extentOf([2, 2, 2]), { min: 2, max: 2 });
    check("extentOf negative only", extentOf([-4, -9, -6]), { min: -9, max: -4 });
    // includeZero only ever widens.
    check("includeZero from above", includeZero({ min: 4, max: 10 }), { min: 0, max: 10 });
    check("includeZero from below", includeZero({ min: -3, max: -1 }), { min: -3, max: 0 });
    check("includeZero straddling", includeZero({ min: -2, max: 5 }), { min: -2, max: 5 });
    // padExtent: the zero-width cases are the ones that would otherwise divide
    // by zero downstream.
    check("padExtent leaves a real extent alone", padExtent({ min: 0, max: 10 }), { min: 0, max: 10 });
    check("padExtent constant positive", padExtent({ min: 2, max: 2 }), { min: 1, max: 3 });
    check("padExtent constant negative", padExtent({ min: -4, max: -4 }), { min: -6, max: -2 });
    check("padExtent constant zero", padExtent({ min: 0, max: 0 }), { min: -0.5, max: 0.5 });
    check("scaleLinear midpoint", scaleLinear({ min: 0, max: 10 }, 0, 100)(2.5), 25);
    check("scaleLinear at the bounds", [scaleLinear({ min: 0, max: 10 }, 0, 100)(0), scaleLinear({ min: 0, max: 10 }, 0, 100)(10)], [0, 100]);
    // An inverted range is the normal case for a screen y axis: the domain's
    // maximum belongs at the TOP, which is y = 0.
    check("scaleLinear inverted range", scaleLinear({ min: 0, max: 10 }, 100, 0)(2.5), 75);
    check("scaleLinear extrapolates", scaleLinear({ min: 0, max: 10 }, 0, 100)(15), 150);
    check("scaleLinear negative domain", scaleLinear({ min: -10, max: 10 }, 0, 100)(-5), 25);
    // Zero-width domain: every input, including one outside it, lands in the
    // middle of the range. Never NaN.
    check("scaleLinear zero-width domain", scaleLinear({ min: 5, max: 5 }, 0, 100)(5), 50);
    check("scaleLinear zero-width domain, off-domain input", scaleLinear({ min: 5, max: 5 }, 0, 100)(999), 50);
    check("tickStep 10 over 5", tickStep(10, 5), 2);
    check("tickStep 1 over 5", tickStep(1, 5), 0.2);
    // 24.25 rounded UP to a nice number is 50, which would leave two labels on
    // the axis. The geometric threshold picks 20 and leaves five.
    check("tickStep 97 over 4", tickStep(97, 4), 20);
    check("tickStep 800 over 10", tickStep(800, 10), 100);
    check("tickStep refuses a zero span", tickStep(0, 5), 0);
    check("tickStep refuses a zero count", tickStep(10, 0), 0);
    check("niceTicks 0..10", niceTicks({ min: 0, max: 10 }, 5), [0, 2, 4, 6, 8, 10]);
    // The fractional case is the one that exposes binary accumulation: n * 0.2
    // is 0.6000000000000001 at n = 3.
    check("niceTicks 0..1", niceTicks({ min: 0, max: 1 }, 5), [0, 0.2, 0.4, 0.6, 0.8, 1]);
    check("niceTicks 0..97", niceTicks({ min: 0, max: 97 }, 4), [0, 20, 40, 60, 80]);
    // Ticks land INSIDE the extent: 3 and 17 are not multiples of 5.
    check("niceTicks 3..17", niceTicks({ min: 3, max: 17 }, 4), [5, 10, 15]);
    check("niceTicks across zero", niceTicks({ min: -10, max: 10 }, 4), [-10, -5, 0, 5, 10]);
    check("niceTicks zero-width extent", niceTicks({ min: 5, max: 5 }, 4), [5]);
    check("niceTicks zero-width extent at zero", niceTicks({ min: 0, max: 0 }, 4), [0]);
    check("polylinePath empty", polylinePath([]), "");
    check("polylinePath all gaps", polylinePath([null, null]), "");
    check("polylinePath two points", polylinePath([{ x: 0, y: 0 }, { x: 10, y: 5 }]), "M0 0L10 5");
    check("polylinePath rounds to two decimals", polylinePath([{ x: 1.234, y: 5.678 }, { x: 2, y: 3 }]), "M1.23 5.68L2 3");
    // A gap starts a new subpath. The lone point before it becomes a zero-length
    // line so that stroke-linecap="round" draws it as a dot.
    check("polylinePath breaks at a gap", polylinePath([{ x: 0, y: 0 }, null, { x: 10, y: 5 }, { x: 20, y: 5 }]), "M0 0L0 0M10 5L20 5");
    check("polylinePath lone trailing point", polylinePath([null, { x: 4, y: 4 }]), "M4 4L4 4");
    check("polylinePath treats NaN as a gap", polylinePath([{ x: 0, y: 0 }, { x: Number.NaN, y: 1 }, { x: 2, y: 2 }]), "M0 0L0 0M2 2L2 2");
    // NaN is in here beside the null on purpose: it is the gap a *caller* makes
    // — a rate over a zero denominator — and it is the one that survives a
    // null-only guard. Left in, it becomes the lowest AND the highest reading of
    // every series it appears in, because every comparison against NaN is false.
    check("measuredPoints drops gaps, including non-finite ones", measuredPoints([
        { label: "a", value: 1 },
        { label: "b", value: null },
        { label: "c", value: Number.NaN },
        { label: "d", value: 0 },
    ]), [{ label: "a", value: 1 }, { label: "d", value: 0 }]);
    check("describeSeries counts a NaN as unmeasured", describeSeries("Failure rate", [
        { label: "Mon", value: 0.5 },
        { label: "Tue", value: Number.NaN },
        { label: "Wed", value: 0.25 },
    ]), "Failure rate: 2 of 3 points measured, Mon to Wed. Starts at 0.5, ends at 0.25. " +
        "Lowest 0.25 at Wed, highest 0.5 at Mon.");
    check("describeSeries with no data", describeSeries("Requests", [{ label: "a", value: null }]), "Requests: no data.");
    check("describeSeries complete", describeSeries("Requests", [
        { label: "Mon", value: 12 },
        { label: "Tue", value: 51 },
        { label: "Wed", value: 3 },
        { label: "Thu", value: 40 },
    ]), "Requests: 4 points, Mon to Thu. Starts at 12, ends at 40. Lowest 3 at Wed, highest 51 at Tue.");
    // The gap is counted out loud, the first/last are the first/last MEASURED
    // points, and a tie names the earliest.
    check("describeSeries with a gap and a tie", describeSeries("Failures", [
        { label: "Mon", value: null },
        { label: "Tue", value: 7 },
        { label: "Wed", value: 7 },
        { label: "Thu", value: 1 },
    ], (value) => `${value} failed`), "Failures: 3 of 4 points measured, Tue to Thu. Starts at 7 failed, ends at 1 failed. " +
        "Lowest 1 failed at Thu, highest 7 failed at Tue.");
    /* -- US-016: slots, folding, bands, stacks, paths, arcs, summaries ------ */
    // Slots are handed out in fixed order and never cycled: a seventh series
    // gets the neutral, not slot 1 again.
    check("chartSlotColor first and last slots", [chartSlotColor(0), chartSlotColor(5)], ["var(--esm-chart-1)", "var(--esm-chart-6)"]);
    check("chartSlotColor never cycles", [chartSlotColor(6), chartSlotColor(11), chartSlotColor(-1), chartSlotColor(1.5)], ["var(--esm-chart-other)", "var(--esm-chart-other)", "var(--esm-chart-other)", "var(--esm-chart-other)"]);
    check("foldToOther leaves a short series alone", foldToOther([{ label: "a", value: 1 }, { label: "b", value: 2 }], 3), {
        points: [{ label: "a", value: 1 }, { label: "b", value: 2 }],
        folded: 0,
        droppedUnmeasured: 0,
    });
    // The kept rows come back in INPUT order (a before c), not in rank order
    // (c before a) — otherwise the slot colours would follow the ranking.
    check("foldToOther keeps the largest, in input order", foldToOther([
        { label: "a", value: 5 },
        { label: "b", value: 1 },
        { label: "c", value: 9 },
        { label: "d", value: 2 },
        { label: "e", value: null },
        { label: "f", value: 3 },
    ], 3), {
        points: [{ label: "a", value: 5 }, { label: "c", value: 9 }, { label: "Other", value: 6 }],
        folded: 3,
        droppedUnmeasured: 1,
    });
    check("foldToOther breaks a tie towards the earlier point", foldToOther([
        { label: "a", value: 5 },
        { label: "b", value: 5 },
        { label: "c", value: 1 },
    ], 2), {
        points: [{ label: "a", value: 5 }, { label: "Other", value: 6 }],
        folded: 2,
        droppedUnmeasured: 0,
    });
    // An unmeasured point past the cut is DROPPED and counted, never summed
    // into Other — where it would have become a zero contribution nobody can
    // see. With nothing measurable in the tail there is no Other row at all.
    check("foldToOther never folds a gap into the sum", foldToOther([
        { label: "a", value: 5 },
        { label: "b", value: null },
        { label: "c", value: Number.NaN },
    ], 2), {
        points: [{ label: "a", value: 5 }],
        folded: 0,
        droppedUnmeasured: 2,
    });
    check("bandPositions centres each band in its slot", bandPositions(4, 0, 100), [
        { start: 1, size: 23, center: 12.5 },
        { start: 26, size: 23, center: 37.5 },
        { start: 51, size: 23, center: 62.5 },
        { start: 76, size: 23, center: 87.5 },
    ]);
    // Two bars across a wide card are two bars, not two slabs: the cap wins.
    check("bandPositions caps the thickness", bandPositions(2, 0, 100), [
        { start: 13, size: 24, center: 25 },
        { start: 63, size: 24, center: 75 },
    ]);
    // A slot too tight to hold the gap keeps a proportional sliver rather than
    // collapsing to nothing — a chart that stops drawing past N bars is worse
    // than a thin one.
    check("bandPositions survives slots tighter than the gap", bandPositions(5, 0, 5).map((band) => band.size), [0.6, 0.6, 0.6, 0.6, 0.6]);
    check("bandPositions still centres the sliver", bandPositions(5, 0, 5)[0], { start: 0.2, size: 0.6, center: 0.5 });
    check("bandPositions refuses a zero count", bandPositions(0, 0, 100), []);
    check("bandPositions refuses an inverted range", bandPositions(4, 100, 0), []);
    // The last index is always labelled, because the stride counts back from it.
    check("labelledIndices always includes the last", labelledIndices(30, 6), [4, 9, 14, 19, 24, 29]);
    check("labelledIndices labels everything when it fits", labelledIndices(5, 10), [0, 1, 2, 3, 4]);
    check("labelledIndices of one", labelledIndices(1, 6), [0]);
    check("labelledIndices of none", labelledIndices(0, 6), []);
    // A gap in a stack leaves a hole in the SEGMENT list, not a shift: segment
    // i is always series i, which is what makes stack position an encoding. A
    // measured zero is a real segment with start === end; a negative part has
    // no length on a part-of-a-whole axis and is reported instead of drawn.
    check("stackValues holds position through gaps", stackValues("Mon", [
        { label: "a", value: 7 },
        { label: "b", value: null },
        { label: "c", value: 3 },
        { label: "d", value: -1 },
        { label: "e", value: 0 },
    ]), {
        label: "Mon",
        segments: [
            { label: "a", value: 7, start: 0, end: 7 },
            null,
            { label: "c", value: 3, start: 7, end: 10 },
            null,
            { label: "e", value: 0, start: 10, end: 10 },
        ],
        total: 10,
        unmeasured: 1,
        undrawable: 1,
    });
    check("stackValues of nothing", stackValues("Mon", []), {
        label: "Mon",
        segments: [],
        total: 0,
        unmeasured: 0,
        undrawable: 0,
    });
    check("barPath square", barPath(0, 0, 10, 4, 2, "none"), "M0 0L10 0L10 4L0 4Z");
    // The radius is ignored on "none" — an interior stack segment has no free
    // end to round, and rounding it would read as the top of the stack.
    check("barPath ignores the radius without a data end", barPath(0, 0, 10, 4, 4, "none"), "M0 0L10 0L10 4L0 4Z");
    check("barPath rounds the right-hand data end", barPath(0, 0, 100, 20, 4, "right"), "M0 0L96 0Q100 0 100 4L100 16Q100 20 96 20L0 20Z");
    check("barPath rounds the top data end", barPath(0, 10, 20, 90, 4, "top"), "M0 100L0 14Q0 10 4 10L16 10Q20 10 20 14L20 100Z");
    check("barPath rounds the bottom data end", barPath(0, 0, 10, 4, 4, "bottom"), "M0 0L10 0L10 2Q10 4 8 4L2 4Q0 4 0 2Z");
    check("barPath rounds the left data end", barPath(0, 0, 10, 4, 2, "left"), "M10 0L10 4L2 4Q0 4 0 2L0 2Q0 0 2 0Z");
    // A radius larger than the bar is clamped to half its short side, so a
    // one-request day is a thin bar and not a lens.
    check("barPath clamps the radius to the bar", barPath(0, 0, 10, 2, 4, "top"), "M0 2L0 1Q0 0 1 0L9 0Q10 0 10 1L10 2Z");
    check("barPath refuses a bar with no area", [barPath(0, 0, 10, 0, 4, "top"), barPath(0, 0, -5, 10, 4, "top")], ["", ""]);
    check("barPath refuses a non-finite bar", barPath(0, Number.NaN, 10, 4, 2, "top"), "");
    // Angles run clockwise from twelve o'clock, which is where a reader starts
    // a ring. A quarter turn therefore ends at three o'clock.
    check("arcPath quarter ring", arcPath(0, 0, 10, 5, 0, Math.PI / 2), "M0 -10A10 10 0 0 1 10 0L5 0A5 5 0 0 0 0 -5Z");
    check("arcPath sets the large-arc flag past a half turn", arcPath(0, 0, 10, 5, 0, Math.PI * 1.5), "M0 -10A10 10 0 1 1 -10 0L-5 0A5 5 0 1 0 0 -5Z");
    check("arcPath with no hole is a wedge", arcPath(0, 0, 10, 0, 0, Math.PI / 2), "M0 0L0 -10A10 10 0 0 1 10 0Z");
    // A whole turn is two counter-wound circles: one elliptical arc of exactly
    // 360 degrees starts and ends at the same point and draws nothing.
    check("arcPath full ring is two counter-wound circles", arcPath(50, 50, 40, 20, 0, Math.PI * 2), "M50 10A40 40 0 1 1 50 90A40 40 0 1 1 50 10ZM50 30A20 20 0 1 0 50 70A20 20 0 1 0 50 30Z");
    check("arcPath refuses a zero span", arcPath(0, 0, 10, 5, 1, 1), "");
    check("arcPath refuses a backwards span", arcPath(0, 0, 10, 5, 2, 1), "");
    check("arcPath refuses a zero radius", arcPath(0, 0, 0, 0, 0, 1), "");
    check("donutArcs divides the turn in input order", donutArcs([
        { label: "a", value: 1 },
        { label: "b", value: 1 },
        { label: "c", value: 1 },
        { label: "d", value: 1 },
    ]).arcs.map((arc) => (arc === null ? null : arc.startAngle / Math.PI)), [0, 0.5, 1, 1.5]);
    // The arc list keeps the input's POSITIONS. A dense list would shift "c"
    // onto the hue "b" was drawn in, so the legend and the ring would disagree
    // about which slice is which — with nothing on screen saying so.
    check("donutArcs keeps a gap in place rather than closing it", donutArcs([
        { label: "a", value: 1 },
        { label: "b", value: null },
        { label: "c", value: 1 },
    ]).arcs.map((arc) => (arc === null ? null : arc.label)), ["a", null, "c"]);
    // Nothing to divide: no arcs at all. A ring drawn anyway would show one
    // slice at 100% of a day that did not happen.
    check("donutArcs of an all-zero composition", donutArcs([{ label: "a", value: 0 }, { label: "b", value: 0 }]), {
        arcs: [null, null],
        measured: 2,
        total: 0,
        unmeasured: 0,
        undrawable: 0,
        tooSmall: 0,
    });
    check("donutArcs counts gaps and negatives without drawing them", donutArcs([
        { label: "a", value: 5 },
        { label: "b", value: null },
        { label: "c", value: -2 },
    ]), {
        arcs: [{ label: "a", value: 5, share: 1, startAngle: 0, endAngle: Math.PI * 2, drawable: true }, null, null],
        measured: 1,
        total: 5,
        unmeasured: 1,
        undrawable: 1,
        tooSmall: 0,
    });
    // A lone slice takes no inset: it is the whole ring and has no neighbour to
    // be separated from, so a gap would render 100% with a notch cut out of it.
    check("donutArcs gives a lone slice the whole turn", donutArcs([{ label: "a", value: 5 }], { gapAngle: 0.4 }).arcs.map((arc) => [arc?.startAngle, arc?.endAngle]), [[0, Math.PI * 2]]);
    check("donutArcs insets both ends of every neighbouring slice", donutArcs([
        { label: "a", value: 1 },
        { label: "b", value: 1 },
    ], { gapAngle: 0.2 }).arcs.map((arc) => [Number(arc?.startAngle.toFixed(3)), Number(arc?.endAngle.toFixed(3))]), [
        [0.1, 3.042],
        [3.242, 6.183],
    ]);
    // A slice thinner than the gap keeps its true share and is simply not
    // drawn; the count is what the chart says out loud.
    check("donutArcs reports the slices too thin to draw", (() => {
        const composition = donutArcs([{ label: "a", value: 999 }, { label: "b", value: 1 }], { gapAngle: 0.1 });
        return [composition.tooSmall, composition.arcs.map((arc) => arc?.drawable)];
    })(), [1, [true, false]]);
    // A measured zero is not "too thin" — it is zero, and it has a legend row.
    check("donutArcs does not call a real zero too thin", donutArcs([{ label: "a", value: 5 }, { label: "b", value: 0 }]).tooSmall, 0);
    check("sharePercent to one decimal", [sharePercent(1, 4), sharePercent(1, 3)], [25, 33.3]);
    check("sharePercent of no whole", [sharePercent(0, 0), sharePercent(5, 0), sharePercent(Number.NaN, 10)], [null, null, null]);
    // A bar's length IS its reading, so its axis starts at zero — padExtent's
    // "put the constant in the middle of a band" is right for a line and wrong
    // here, and an all-zero day must draw as empty bars, not as a baseline
    // floating halfway up the chart.
    check("barDomain anchors at zero from above", barDomain([4, 10]), { min: 0, max: 10 });
    check("barDomain keeps a negative reach", barDomain([-3, 5]), { min: -3, max: 5 });
    check("barDomain of an all-zero series", barDomain([0, 0]), { min: 0, max: 1 });
    check("barDomain of a series with nothing measured", barDomain([null, Number.NaN]), { min: 0, max: 1 });
    check("barDomain of a constant series", barDomain([7, 7]), { min: 0, max: 7 });
    // A series shorter than the labels is not measured for the bars it does not
    // reach — the reading a dashboard gets when a day predates the series.
    check("stackBars transposes and pads short series with gaps, not zeros", stackBars(["Mon", "Tue"], [
        { name: "Succeeded", points: [{ label: "Mon", value: 8 }, { label: "Tue", value: 5 }] },
        { name: "Failed", points: [{ label: "Mon", value: 2 }] },
    ]), [
        {
            label: "Mon",
            segments: [
                { label: "Succeeded", value: 8, start: 0, end: 8 },
                { label: "Failed", value: 2, start: 8, end: 10 },
            ],
            total: 10,
            unmeasured: 0,
            undrawable: 0,
        },
        {
            label: "Tue",
            segments: [{ label: "Succeeded", value: 5, start: 0, end: 5 }, null],
            total: 5,
            unmeasured: 1,
            undrawable: 0,
        },
    ]);
    check("describeCategories names the ends without implying an order", describeCategories("Requests by model", [
        { label: "gpt", value: 900 },
        { label: "claude", value: null },
        { label: "llama", value: 100 },
    ]), "Requests by model: 2 of 3 categories measured, 1000 in total. Highest gpt at 900, lowest llama at 100.");
    // Same tie rule as describeSeries, and it needs its own fixture: the two
    // comparisons are written afresh here, so a `>=` that names the LAST
    // category to reach the value is invisible to every non-tied series.
    check("describeCategories breaks a tie towards the first category", describeCategories("Requests", [{ label: "a", value: 7 }, { label: "b", value: 7 }, { label: "c", value: 1 }]), "Requests: 3 categories, 15 in total. Highest a at 7, lowest c at 1.");
    check("describeCategories of one", describeCategories("Requests", [{ label: "a", value: 4 }]), "Requests: 1 category. a at 4.");
    check("describeCategories of nothing", describeCategories("Requests", []), "Requests: no data.");
    check("describeComposition", describeComposition("Revenue by application", [{ label: "A", value: 700 }, { label: "B", value: 300 }]), "Revenue by application: 2 parts, 1000 in total. Largest A, 700, 70%. Smallest B, 300, 30%.");
    // Told the gap the ring is actually drawn with, the summary counts the same
    // undrawn slices the legend marks. Told nothing, it would report a complete
    // picture beside a legend that says otherwise.
    check("describeComposition counts the slices the ring cannot draw", describeComposition("Revenue by application", [{ label: "A", value: 999 }, { label: "B", value: 1 }], (value) => String(value), { gapAngle: 0.1 }), "Revenue by application: 2 parts, 1000 in total. Largest A, 999, 99.9%. Smallest B, 1, 0.1%." +
        " 1 too thin to draw; the table has them.");
    // The zero-total ring says so in words. This is the sentence that replaces
    // a full ring drawn from nothing.
    check("describeComposition of an all-zero composition", describeComposition("Revenue", [{ label: "A", value: 0 }, { label: "B", value: 0 }]), "Revenue: 2 parts, all zero — there is no whole to divide.");
    check("describeComposition with nothing measured", describeComposition("Revenue", [{ label: "A", value: null }]), "Revenue: nothing measured in this window.");
    check("describeComposition of nothing", describeComposition("Revenue", []), "Revenue: no data.");
    check("describeStacks states the stacking order and counts the holes", describeStacks("Requests", ["Succeeded", "Failed"], [
        stackValues("Mon", [{ label: "Succeeded", value: 8 }, { label: "Failed", value: 2 }]),
        stackValues("Tue", [{ label: "Succeeded", value: 5 }, { label: "Failed", value: null }]),
    ]), "Requests: 2 bars, Mon to Tue, 15 in total. Stacked bottom to top: Succeeded, Failed. " +
        "Largest Mon at 10. 1 of 4 segments not measured.");
    check("describeStacks of a single bar", describeStacks("Requests", ["Succeeded"], [stackValues("Mon", [{ label: "Succeeded", value: 8 }])]), "Requests: 1 bar, Mon, 8 in total. Stacked bottom to top: Succeeded. Largest Mon at 8.");
    check("describeStacks of nothing", describeStacks("Requests", ["Succeeded"], []), "Requests: no data.");
    // groupBars is stackBars' twin: positionally aligned, short series read as
    // NOT MEASURED, and — the difference that matters — a negative reading is a
    // bar rather than a hole.
    check("groupBars transposes and keeps negatives", groupBars(["Mon", "Tue"], [
        { name: "Revenue", points: [{ label: "Mon", value: 10 }, { label: "Tue", value: 4 }] },
        { name: "Cost", points: [{ label: "Mon", value: 6 }, { label: "Tue", value: 9 }] },
        { name: "Margin", points: [{ label: "Mon", value: 4 }, { label: "Tue", value: -5 }] },
    ]), [
        {
            label: "Mon",
            bars: [
                { label: "Revenue", value: 10 },
                { label: "Cost", value: 6 },
                { label: "Margin", value: 4 },
            ],
            unmeasured: 0,
        },
        {
            label: "Tue",
            bars: [
                { label: "Revenue", value: 4 },
                { label: "Cost", value: 9 },
                { label: "Margin", value: -5 },
            ],
            unmeasured: 0,
        },
    ]);
    // A hole keeps its POSITION. A dense array here would draw Margin in Cost's
    // slot and in Cost's hue, with the legend still claiming otherwise.
    check("groupBars keeps a gap in position", groupBars(["Mon"], [
        { name: "Revenue", points: [{ label: "Mon", value: 10 }] },
        { name: "Cost", points: [{ label: "Mon", value: null }] },
        { name: "Margin", points: [{ label: "Mon", value: Number.NaN }] },
    ]), [{ label: "Mon", bars: [{ label: "Revenue", value: 10 }, null, null], unmeasured: 2 }]);
    check("groupBars reads a short series as not measured", groupBars(["Mon", "Tue"], [{ name: "Revenue", points: [{ label: "Mon", value: 3 }] }]), [
        { label: "Mon", bars: [{ label: "Revenue", value: 3 }], unmeasured: 0 },
        { label: "Tue", bars: [null], unmeasured: 1 },
    ]);
    check("groupBars of no labels", groupBars([], [{ name: "Revenue", points: [] }]), []);
    // Zero IS a reading, and it is not a gap.
    check("groupBars keeps a zero", groupBars(["Mon"], [{ name: "Revenue", points: [{ label: "Mon", value: 0 }] }]), [{ label: "Mon", bars: [{ label: "Revenue", value: 0 }], unmeasured: 0 }]);
    // describeGroups totals per SERIES, and a negative is summed rather than
    // dropped: a loss-making day is the reading the money chart exists for.
    check("describeGroups totals per series", describeGroups("Money", ["Revenue", "Cost", "Margin"], groupBars(["Mon", "Tue"], [
        { name: "Revenue", points: [{ label: "Mon", value: 10 }, { label: "Tue", value: 4 }] },
        { name: "Cost", points: [{ label: "Mon", value: 6 }, { label: "Tue", value: 9 }] },
        { name: "Margin", points: [{ label: "Mon", value: 4 }, { label: "Tue", value: -5 }] },
    ])), "Money: 2 groups of 3 bars, Mon to Tue, sharing one axis." +
        " Totals: Revenue 14, Cost 15, Margin -1.");
    check("describeGroups counts the holes and names a series with none", describeGroups("Money", ["Revenue", "Cost"], groupBars(["Mon"], [
        { name: "Revenue", points: [{ label: "Mon", value: 10 }] },
        { name: "Cost", points: [{ label: "Mon", value: null }] },
    ])), "Money: 1 group of 2 bars, Mon, sharing one axis." +
        " Totals: Revenue 10, Cost not measured. 1 of 2 bars not measured.");
    check("describeGroups of nothing", describeGroups("Money", ["Revenue"], []), "Money: no data.");
    check("describeGroups with no series", describeGroups("Money", [], groupBars(["Mon"], [])), "Money: no data.");
    console.log(`charts-scale self-check: ${passed} checks passed`);
}
if (import.meta.main === true) {
    selfCheck();
}
//# sourceMappingURL=charts-scale.js.map